import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { showToast } from "../../lib/toast";
import {
  ReceiveTransferItemDto,
  StockTransfer,
  stockTransferService,
} from "../../services/stock-transfer.service";

export function ReceiveStockTransferPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [transfer, setTransfer] = useState<StockTransfer | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");
  const [receiveItems, setReceiveItems] = useState<
    Record<
      string,
      { qtyReceived: number; qtyRejected: number; rejectionReason: string }
    >
  >({});

  useEffect(() => {
    if (id) loadTransfer();
  }, [id]);

  const loadTransfer = async () => {
    try {
      setLoading(true);
      const data = await stockTransferService.getById(id!);
      setTransfer(data);

      const items: Record<
        string,
        { qtyReceived: number; qtyRejected: number; rejectionReason: string }
      > = {};
      data.items?.forEach((item) => {
        items[item.id] = {
          qtyReceived: item.qtySent,
          qtyRejected: 0,
          rejectionReason: "",
        };
      });
      setReceiveItems(items);
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to load transfer");
      navigate("/stock-transfers");
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (
    itemId: string,
    qtySent: number,
    field: "qtyReceived" | "qtyRejected" | "rejectionReason",
    value: number | string,
  ) => {
    setReceiveItems((prev) => {
      const current = prev[itemId] ?? {
        qtyReceived: qtySent,
        qtyRejected: 0,
        rejectionReason: "",
      };

      if (field === "rejectionReason") {
        return {
          ...prev,
          [itemId]: { ...current, rejectionReason: String(value) },
        };
      }

      const numericValue = Math.min(
        qtySent,
        Math.max(0, Number.isFinite(Number(value)) ? Number(value) : 0),
      );

      if (field === "qtyReceived") {
        return {
          ...prev,
          [itemId]: {
            ...current,
            qtyReceived: numericValue,
            qtyRejected: qtySent - numericValue,
          },
        };
      }

      return {
        ...prev,
        [itemId]: {
          ...current,
          qtyRejected: numericValue,
          qtyReceived: qtySent - numericValue,
        },
      };
    });
  };

  const handleSubmit = async () => {
    if (!transfer?.items?.length) return;

    for (const item of transfer.items) {
      const receiveItem = receiveItems[item.id];
      const qtyReceived = receiveItem?.qtyReceived ?? 0;
      const qtyRejected = receiveItem?.qtyRejected ?? 0;

      if (qtyReceived + qtyRejected !== item.qtySent) {
        showToast.error(
          `${item.product?.name || "Item"}: received and rejected quantities must equal ${item.qtySent}`,
        );
        return;
      }

      if (qtyRejected > 0 && !receiveItem?.rejectionReason?.trim()) {
        showToast.error(
          `${item.product?.name || "Item"}: rejection reason is required when rejecting stock`,
        );
        return;
      }
    }

    const items: ReceiveTransferItemDto[] = transfer.items.map((item) => ({
      itemId: item.id,
      qtyReceived: receiveItems[item.id]?.qtyReceived ?? 0,
      qtyRejected: receiveItems[item.id]?.qtyRejected ?? 0,
      rejectionReason: receiveItems[item.id]?.rejectionReason || undefined,
    }));

    setSubmitting(true);
    try {
      await stockTransferService.receive(id!, {
        items,
        notes: notes || undefined,
      });
      showToast.success("Stock transfer received successfully");
      navigate("/stock-transfers");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to receive transfer",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500 py-12">
          Loading transfer details...
        </div>
      </div>
    );
  }

  if (!transfer) return null;

  const canReceive = transfer.status === "IN_TRANSIT";
  const totals = transfer.items?.reduce(
    (summary, item) => {
      summary.sent += item.qtySent;
      summary.received += receiveItems[item.id]?.qtyReceived ?? 0;
      summary.rejected += receiveItems[item.id]?.qtyRejected ?? 0;
      return summary;
    },
    { sent: 0, received: 0, rejected: 0 },
  ) ?? { sent: 0, received: 0, rejected: 0 };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Receive Stock Transfer
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Transfer #{transfer.transferNumber}
          </p>
        </div>
        <button
          onClick={() => navigate("/stock-transfers")}
          className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Back
        </button>
      </div>

      {/* Transfer Info */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Source Store</p>
            <p className="font-medium">{transfer.sourceStore?.name || "—"}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Destination Store</p>
            <p className="font-medium">{transfer.destStore?.name || "—"}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
              {transfer.status.replace("_", " ")}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-500">Transfer Date</p>
            <p className="font-medium">
              {new Date(transfer.transferDate).toLocaleDateString()}
            </p>
          </div>
        </div>
        {transfer.notes && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-gray-500">Notes</p>
            <p className="text-sm">{transfer.notes}</p>
          </div>
        )}
      </div>

      {!canReceive && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800">
            This transfer cannot be received. Only transfers with status "IN
            TRANSIT" can be received. Current status:{" "}
            <strong>{transfer.status.replace("_", " ")}</strong>
          </p>
        </div>
      )}

      {/* Items Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Transfer Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Product
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Batch
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Qty Sent
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Qty Received
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Qty Rejected
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Rejection Reason
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {transfer.items?.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-sm">
                      {item.product?.name || "—"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.product?.sku}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {item.batch?.batchNumber || "—"}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium">
                    {item.qtySent}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="number"
                      min={0}
                      max={item.qtySent}
                      value={receiveItems[item.id]?.qtyReceived ?? 0}
                      onChange={(e) =>
                        updateItem(
                          item.id,
                          item.qtySent,
                          "qtyReceived",
                          Math.max(0, parseInt(e.target.value) || 0),
                        )
                      }
                      disabled={!canReceive}
                      className="w-20 px-2 py-1 text-right border rounded text-sm disabled:bg-gray-100"
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="number"
                      min={0}
                      max={item.qtySent}
                      value={receiveItems[item.id]?.qtyRejected ?? 0}
                      onChange={(e) =>
                        updateItem(
                          item.id,
                          item.qtySent,
                          "qtyRejected",
                          Math.max(0, parseInt(e.target.value) || 0),
                        )
                      }
                      disabled={!canReceive}
                      className="w-20 px-2 py-1 text-right border rounded text-sm disabled:bg-gray-100"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={receiveItems[item.id]?.rejectionReason ?? ""}
                      onChange={(e) =>
                        updateItem(
                          item.id,
                          item.qtySent,
                          "rejectionReason",
                          e.target.value,
                        )
                      }
                      disabled={
                        !canReceive ||
                        (receiveItems[item.id]?.qtyRejected ?? 0) === 0
                      }
                      placeholder="Reason..."
                      className="w-full px-2 py-1 border rounded text-sm disabled:bg-gray-100"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes & Submit */}
      {canReceive && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg bg-gray-50 px-4 py-3">
              <div className="text-gray-500">Qty Sent</div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                {totals.sent}
              </div>
            </div>
            <div className="rounded-lg bg-green-50 px-4 py-3">
              <div className="text-green-700">Qty Received</div>
              <div className="mt-1 text-lg font-semibold text-green-900">
                {totals.received}
              </div>
            </div>
            <div className="rounded-lg bg-amber-50 px-4 py-3">
              <div className="text-amber-700">Qty Rejected</div>
              <div className="mt-1 text-lg font-semibold text-amber-900">
                {totals.rejected}
              </div>
            </div>
          </div>
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Rejected quantities are automatically returned to the source store.
            Enter a rejection reason for every rejected line.
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Receiving Notes
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Any notes about this receipt..."
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => navigate("/stock-transfers")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
            >
              {submitting ? "Processing..." : "Confirm Receipt"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
